import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { useTranslation } from 'react-i18next';
import { Image } from '../../types/entities/image.entity';
import { galleryThumbSrc, isGalleryVideo } from '../../utils/galleryMedia';

interface GalleryGridProps {
    images: Image[];
    onOpen: (image: Image, index: number) => void;
    onDelete: (image: Image) => void;
    onRemoveFromAlbum?: (image: Image) => void;
}

export const GalleryGrid = ({ images, onOpen, onDelete, onRemoveFromAlbum }: GalleryGridProps) => {
    const { t } = useTranslation();
    const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

    const markBroken = (id: string) => {
        setBrokenIds((prev) => {
            if (prev.has(id)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(4, 1fr)',
                    md: 'repeat(6, 1fr)',
                },
                gap: 1.5,
            }}
        >
            {images.map((image, index) => {
                const thumbSrc = galleryThumbSrc(image);
                const isBroken = brokenIds.has(image.id);
                const isVideo = isGalleryVideo(image);

                return (
                    <Box
                        key={image.id}
                        onClick={() => !isBroken && onOpen(image, index)}
                        sx={{
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: isBroken ? 'default' : 'pointer',
                            bgcolor: 'action.hover',
                            outline: isBroken ? '2px dashed' : 'none',
                            outlineColor: 'warning.main',
                            '&:hover .gallery-grid-delete': {
                                opacity: 1,
                            },
                            '&:hover .gallery-grid-remove': {
                                opacity: 1,
                            },
                            '&:hover .gallery-grid-overlay': {
                                opacity: isBroken ? 0 : 1,
                            },
                        }}
                    >
                        {isBroken ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                    bgcolor: 'grey.100',
                                    p: 1,
                                }}
                            >
                                <BrokenImageOutlinedIcon color="disabled" />
                                <Typography variant="caption" color="text.secondary" textAlign="center">
                                    {t('gallery.unavailable')}
                                </Typography>
                            </Box>
                        ) : thumbSrc ? (
                            <Box
                                component="img"
                                src={thumbSrc}
                                alt=""
                                loading="lazy"
                                onError={() => markBroken(image.id)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        ) : (
                            <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.800' }} />
                        )}

                        {!isBroken && (
                            <Box
                                className="gallery-grid-overlay"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    opacity: isVideo ? 1 : 0,
                                    transition: 'opacity 0.2s',
                                    bgcolor: 'rgba(0,0,0,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                {isVideo && (
                                    <PlayCircleOutlineIcon sx={{ color: 'common.white', fontSize: 48 }} />
                                )}
                            </Box>
                        )}

                        {onRemoveFromAlbum && (
                            <IconButton
                                className="gallery-grid-remove"
                                size="small"
                                aria-label="remove from album"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onRemoveFromAlbum(image);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    color: 'warning.dark',
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: 1,
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                }}
                            >
                                <LinkOffIcon fontSize="small" />
                            </IconButton>
                        )}

                        <IconButton
                            className="gallery-grid-delete"
                            size="small"
                            aria-label="delete"
                            onClick={(event) => {
                                event.stopPropagation();
                                onDelete(image);
                            }}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                color: 'error.main',
                                bgcolor: 'rgba(255,255,255,0.92)',
                                boxShadow: 1,
                                opacity: isBroken ? 1 : 0,
                                transition: 'opacity 0.2s',
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
        </Box>
    );
};
